import { useState } from "react";

/**
 * Wafa Solutions logo.
 *
 *   variant="colour"   → colour on white (header)
 *   variant="knockout" → white / knockout (footer on deep green)
 *
 * The source PNG is green-on-transparent. For the knockout variant we apply
 * `brightness(0) invert(1)` — safe here because the mark is a solid shape.
 */
export function WafaLogo({ variant = "colour", className = "" }) {
  const [failed, setFailed] = useState(false);
  const isKnockout = variant === "knockout";

  if (!failed) {
    return (
      <img
        src="/assets/wafa-logo.png"
        alt="Wafa Solutions"
        onError={() => setFailed(true)}
        data-testid="wafa-logo"
        style={isKnockout ? { filter: "brightness(0) invert(1)" } : undefined}
        className={`h-8 w-auto md:h-10 ${className}`}
      />
    );
  }

  // Text fallback if the file is somehow missing.
  const dot = isKnockout ? "text-white" : "text-wafa-primary";
  const word = isKnockout ? "text-white" : "text-wafa-deep";
  const tail = isKnockout ? "text-white/70" : "text-wafa-deep/60";
  return (
    <span
      data-testid="wafa-logo"
      className={`inline-flex items-baseline gap-1 font-heading font-semibold leading-none ${className}`}
      aria-label="Wafa Solutions LLC"
    >
      <span className={`text-2xl ${word}`}>wafa</span>
      <span className={`text-2xl ${dot}`}>.</span>
      <span className={`text-xs uppercase tracking-widest ${tail}`}>Solutions</span>
    </span>
  );
}

/**
 * AL TALIB GROUP logo — footer only.
 *
 * We serve a pre-processed white-on-transparent PNG (altalib-logo-white.png)
 * rather than relying on CSS `invert()` — the CSS approach loses the very
 * thin Arabic strokes on the wordmark. The pre-processed asset thresholds
 * the alpha and fills white, preserving stroke weight.
 *
 * Sized noticeably larger than the Wafa mark because it is the only logo
 * inside the "Technology partner" panel and needs to read clearly at a
 * glance, including the Arabic wordmark above the Latin one.
 */
export function AltalibLogo({ className = "" }) {
  const [failed, setFailed] = useState(false);
  if (!failed) {
    return (
      <img
        src="/assets/altalib-logo-white.png"
        alt="Altalib Group"
        onError={() => setFailed(true)}
        data-testid="altalib-logo"
        className={`h-12 w-auto md:h-16 ${className}`}
      />
    );
  }
  return (
    <span
      data-testid="altalib-logo"
      className={`inline-flex items-baseline gap-2 font-heading font-semibold leading-none text-white ${className}`}
      aria-label="Altalib Group"
    >
      <span className="text-2xl tracking-[0.25em] uppercase">Altalib</span>
      <span className="text-2xl tracking-[0.25em] uppercase text-white/60">Group</span>
    </span>
  );
}
